import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, qs } from '../lib/api.js';
import { dateTime, num } from '../lib/format.js';
import {
  COMMUNICATION_KIND_LABEL,
  MESSAGE_STATUS_LABEL,
  REACTION_LABEL,
  TX_REASON_LABEL,
  TX_STATUS_LABEL,
  label,
} from '../lib/labels.js';
import { hrefFor } from '../lib/router.js';
import type { Me } from '../lib/session.js';
import { EmptyState, ErrorState, Loading, Panel, StatusBadge } from '../components/ui.js';
import type { Tone } from '../components/ui.js';

interface Item {
  kind: string;
  at: string;
  ref: string;
  brand: string | null;
  status: string | null;
  reason: string | null;
  messageId: string | null;
  requestId: string | null;
  data: Record<string, unknown>;
}
interface Page {
  page: number;
  pageSize: number;
  total: number;
  counts: Record<string, number>;
  items: Item[];
}

const PAGE_SIZE = 25;

const TONE: Record<string, Tone> = {
  sent: 'ok',
  delivered: 'ok',
  logged: 'neu',
  held: 'warn',
  shadow: 'sim',
  dry_run: 'sim',
  queued: 'neu',
  sending: 'neu',
  failed: 'bad',
  unknown: 'bad',
  bounced: 'bad',
  cancelled: 'neu',
};

const reactionFlags = (data: Record<string, unknown>): string =>
  [
    data.delivered ? 'doručeno' : '',
    data.opened ? 'otevřeno' : '',
    data.clicked ? 'proklik' : '',
    data.bounced ? 'odskok' : '',
    data.unsubscribed ? 'odhlášení' : '',
  ]
    .filter(Boolean)
    .join(', ');

/** Karta zákazníka: celá historie komunikace na jedné ose (cesty, transakční zprávy, e-shop, import, podpora, reakce). */
export function CustomerCommunication({
  contactId,
  me,
}: {
  contactId: string;
  me: Me;
}): JSX.Element {
  const [kind, setKind] = useState('');
  const [page, setPage] = useState(1);
  const query = useQuery<Page>({
    queryKey: ['customer-communication', contactId, kind, page],
    queryFn: () =>
      api.get(
        `/api/customers/${contactId}/communication${qs({ kind, page, pageSize: PAGE_SIZE })}`,
      ),
  });
  const data = query.data;
  const pages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  return (
    <Panel
      title="Historie komunikace"
      subtitle="Všechno, co kontaktu hub poslal nebo zaznamenal: cesty a marketing, transakční zprávy z e-shopu, zprávy odeslané e-shopem, import ze SmartEmailingu, podpora (jen údaje o kontaktu, bez textů) a reakce."
      action={
        <label className="field">
          <span>Druh</span>
          <select
            value={kind}
            onChange={(e) => {
              setKind(e.target.value);
              setPage(1);
            }}
          >
            <option value="">
              Všechno
              {data ? ` (${num(Object.values(data.counts).reduce((a, b) => a + b, 0))})` : ''}
            </option>
            {Object.entries(COMMUNICATION_KIND_LABEL).map(([k, text]) => (
              <option key={k} value={k}>
                {text}
                {data ? ` (${num(data.counts[k] ?? 0)})` : ''}
              </option>
            ))}
          </select>
        </label>
      }
    >
      {query.isLoading ? <Loading what="historii komunikace" /> : null}
      {query.isError ? (
        <ErrorState
          title="Historii se nepodařilo načíst"
          error={query.error}
          onRetry={() => void query.refetch()}
        />
      ) : null}
      {data && data.items.length === 0 ? (
        <EmptyState title="Zatím žádná komunikace">
          Zprávy, podpora a reakce se objeví, jakmile se kontaktu něco pošle nebo zaznamená.
        </EmptyState>
      ) : null}
      {data && data.items.length > 0 ? (
        <div className="scroll">
          <table data-testid="communication-history">
            <thead>
              <tr>
                <th>Kdy</th>
                <th>Druh</th>
                <th>Co</th>
                <th>Stav</th>
                <th>Podrobnosti</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((i, idx) => {
                const status = i.status;
                const flags = reactionFlags(i.data);
                return (
                  <tr key={`${i.kind}-${i.at}-${idx}`}>
                    <td>{dateTime(i.at, me.defaultTimezone)}</td>
                    <td>{label(COMMUNICATION_KIND_LABEL, i.kind)}</td>
                    <td>
                      {i.kind === 'reaction' ? (
                        label(REACTION_LABEL, i.ref)
                      ) : i.kind === 'support' ? (
                        i.ref
                      ) : (
                        <code>{i.ref}</code>
                      )}
                      {i.brand ? <span className="muted"> · {i.brand}</span> : null}
                    </td>
                    <td>
                      {status ? (
                        <StatusBadge tone={TONE[status] ?? 'neu'}>
                          {label(
                            i.kind === 'transactional' ? TX_STATUS_LABEL : MESSAGE_STATUS_LABEL,
                            status,
                          )}
                        </StatusBadge>
                      ) : null}
                      {i.reason ? (
                        <div className="muted">{label(TX_REASON_LABEL, i.reason)}</div>
                      ) : null}
                    </td>
                    <td>
                      {flags}
                      {i.requestId ? (
                        <>
                          {flags ? ' · ' : ''}
                          <a href={hrefFor('transactional', i.requestId)}>detail zprávy</a>
                        </>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
      {data && data.total > PAGE_SIZE ? (
        <div className="row">
          <button className="btn" disabled={page <= 1} onClick={() => setPage(page - 1)}>
            Novější
          </button>
          <span className="note">
            Strana {num(page)} z {num(pages)} ({num(data.total)} záznamů)
          </span>
          <button className="btn" disabled={page >= pages} onClick={() => setPage(page + 1)}>
            Starší
          </button>
        </div>
      ) : null}
    </Panel>
  );
}
